/**
 * DOM Utility Functions for Webflow Field Formatting Module
 * 
 * Provides safe, defensive DOM manipulation utilities specifically
 * designed for Webflow form field interactions. Includes comprehensive
 * error handling and graceful degradation.
 */

import { FormattingError, FormattingErrorType } from '../types/formatting-types';

/**
 * Safely query a single element with error handling
 * @param selector CSS selector string
 * @param context Optional context element to search within
 * @returns HTMLElement or null if not found
 */
export function safeQuerySelector(
  selector: string, 
  context: Document | HTMLElement = document
): HTMLElement | null {
  try {
    if (!selector || typeof selector !== 'string') {
      return null;
    }
    return context.querySelector(selector) as HTMLElement;
  } catch (error) {
    console.warn(`[FormattingModule] Invalid selector: ${selector}`, error);
    return null;
  }
}

/**
 * Safely query multiple elements with error handling
 * @param selector CSS selector string
 * @param context Optional context element to search within
 * @returns Array of HTMLElements (empty array if none found)
 */
export function safeQuerySelectorAll(
  selector: string,
  context: Document | HTMLElement = document
): HTMLElement[] {
  try {
    if (!selector || typeof selector !== 'string') {
      return [];
    }
    return Array.from(context.querySelectorAll(selector)) as HTMLElement[];
  } catch (error) {
    console.warn(`[FormattingModule] Invalid selector: ${selector}`, error);
    return [];
  }
}

/**
 * Check if an element is a valid form input field
 * @param element Element to check
 * @returns boolean indicating if element is a valid input
 */
export function isValidFormField(element: Element | null): element is HTMLInputElement | HTMLTextAreaElement {
  if (!element) return false;
  
  const tagName = element.tagName.toLowerCase();
  
  if (tagName === 'input') {
    const inputType = (element as HTMLInputElement).type.toLowerCase();
    // Allow text-like input types
    const validInputTypes = [
      'text', 'tel', 'email', 'number', 'search', 
      'url', 'password', 'date', 'datetime-local'
    ];
    return validInputTypes.includes(inputType);
  }
  
  return tagName === 'textarea';
}

/**
 * Get the current value of a form field safely
 * @param element Form field element
 * @returns Current value or empty string
 */
export function getFieldValue(element: HTMLInputElement | HTMLTextAreaElement): string {
  try {
    return element.value || '';
  } catch (error) {
    console.warn('[FormattingModule] Error getting field value:', error);
    return '';
  }
}

/**
 * Set the value of a form field safely
 * @param element Form field element
 * @param value Value to set
 * @returns boolean indicating success
 */
export function setFieldValue(element: HTMLInputElement | HTMLTextAreaElement, value: string): boolean {
  try {
    if (!element) return false;
    element.value = value || '';
    return true;
  } catch (error) {
    console.warn('[FormattingModule] Error setting field value:', error);
    return false;
  }
}

/**
 * Add a CSS class to an element safely
 * @param element Target element
 * @param className CSS class to add
 * @returns boolean indicating success
 */
export function addCSSClass(element: HTMLElement, className: string): boolean {
  try {
    if (!element || !className) return false;
    element.classList.add(className);
    return true;
  } catch (error) {
    console.warn('[FormattingModule] Error adding CSS class:', error);
    return false;
  }
}

/**
 * Remove a CSS class from an element safely
 * @param element Target element
 * @param className CSS class to remove
 * @returns boolean indicating success
 */
export function removeCSSClass(element: HTMLElement, className: string): boolean {
  try {
    if (!element || !className) return false;
    element.classList.remove(className);
    return true;
  } catch (error) {
    console.warn('[FormattingModule] Error removing CSS class:', error);
    return false;
  }
}

/**
 * Toggle a CSS class on an element safely
 * @param element Target element
 * @param className CSS class to toggle
 * @param force Optional force parameter
 * @returns boolean indicating final state (true = class added)
 */
export function toggleCSSClass(element: HTMLElement, className: string, force?: boolean): boolean {
  try {
    if (!element || !className) return false;
    return element.classList.toggle(className, force);
  } catch (error) {
    console.warn('[FormattingModule] Error toggling CSS class:', error);
    return false;
  }
}

/**
 * Get data attribute value safely
 * @param element Target element
 * @param attributeName Data attribute name (without 'data-' prefix)
 * @returns Attribute value or null
 */
export function getDataAttribute(element: HTMLElement, attributeName: string): string | null {
  try {
    if (!element || !attributeName) return null;
    return element.dataset[attributeName] || null;
  } catch (error) {
    console.warn('[FormattingModule] Error getting data attribute:', error);
    return null;
  }
}

/**
 * Set data attribute value safely
 * @param element Target element
 * @param attributeName Data attribute name (without 'data-' prefix)
 * @param value Value to set
 * @returns boolean indicating success
 */
export function setDataAttribute(element: HTMLElement, attributeName: string, value: string): boolean {
  try {
    if (!element || !attributeName) return false;
    element.dataset[attributeName] = value;
    return true;
  } catch (error) {
    console.warn('[FormattingModule] Error setting data attribute:', error);
    return false;
  }
}

/**
 * Add event listener with error handling
 * @param element Target element
 * @param eventType Event type (e.g., 'input', 'change')
 * @param handler Event handler function
 * @param options Optional event listener options
 * @returns Function to remove the event listener
 */
export function addEventListenerSafe(
  element: HTMLElement,
  eventType: string,
  handler: EventListener,
  options?: AddEventListenerOptions
): (() => void) | null {
  try {
    if (!element || !eventType || !handler) return null;
    
    element.addEventListener(eventType, handler, options);
    
    // Return cleanup function
    return () => {
      try {
        element.removeEventListener(eventType, handler, options);
      } catch (error) {
        console.warn('[FormattingModule] Error removing event listener:', error);
      }
    };
  } catch (error) {
    console.warn('[FormattingModule] Error adding event listener:', error);
    return null;
  }
}

/**
 * Find form fields with specific data attributes
 * @param dataAttribute Data attribute to search for
 * @param context Optional context element to search within
 * @returns Array of matching form field elements
 */
export function findFormFieldsWithAttribute(
  dataAttribute: string,
  context: Document | HTMLElement = document
): HTMLElement[] {
  try {
    if (!dataAttribute) return [];
    
    const selector = `input[data-${dataAttribute}], textarea[data-${dataAttribute}]`;
    const elements = safeQuerySelectorAll(selector, context);
    
    return elements.filter(element => isValidFormField(element));
  } catch (error) {
    console.warn('[FormattingModule] Error finding form fields:', error);
    return [];
  }
}

/**
 * Check if DOM is ready
 * @returns boolean indicating if DOM is ready
 */
export function isDOMReady(): boolean {
  return document.readyState === 'complete' || document.readyState === 'interactive';
}

/**
 * Wait for DOM to be ready
 * @param callback Function to execute when DOM is ready
 */
export function onDOMReady(callback: () => void): void {
  try {
    if (isDOMReady()) {
      callback();
    } else {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    }
  } catch (error) {
    console.warn('[FormattingModule] Error setting up DOM ready callback:', error);
    // Fallback: try to execute callback anyway
    try {
      callback();
    } catch (callbackError) {
      console.error('[FormattingModule] Error executing DOM ready callback:', callbackError);
    }
  }
}

/**
 * Create a debounced version of a function
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | undefined;
  
  return (...args: Parameters<T>) => {
    const later = () => {
      timeout = undefined;
      try {
        func(...args);
      } catch (error) {
        console.warn('[FormattingModule] Error in debounced function:', error);
      }
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Check if element is visible in the viewport
 * @param element Element to check
 * @returns boolean indicating visibility
 */
export function isElementVisible(element: HTMLElement): boolean {
  try {
    if (!element) return false;
    
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= windowHeight &&
      rect.right <= windowWidth &&
      element.offsetParent !== null
    );
  } catch (error) {
    console.warn('[FormattingModule] Error checking element visibility:', error);
    return false;
  }
}

/**
 * Get the cursor position in an input field
 * @param element Input element
 * @returns Object with start and end positions
 */
export function getCursorPosition(element: HTMLInputElement | HTMLTextAreaElement): { start: number; end: number } {
  try {
    return {
      start: element.selectionStart || 0,
      end: element.selectionEnd || 0
    };
  } catch (error) {
    console.warn('[FormattingModule] Error getting cursor position:', error);
    return { start: 0, end: 0 };
  }
}

/**
 * Set the cursor position in an input field
 * @param element Input element
 * @param start Start position
 * @param end End position (optional, defaults to start)
 * @returns boolean indicating success
 */
export function setCursorPosition(
  element: HTMLInputElement | HTMLTextAreaElement,
  start: number,
  end: number = start
): boolean {
  try {
    if (!element) return false;
    element.setSelectionRange(start, end);
    return true;
  } catch (error) {
    console.warn('[FormattingModule] Error setting cursor position:', error);
    return false;
  }
}